/**
 * Der Zustand — alles, was ein Mensch verstellen kann, und nur das.
 *
 * Flach, serialisierbar, ohne Funktionen und ohne Verweise auf die Szene. Ein
 * Zustand ist damit ein Szenario: speicherbar, vergleichbar, in einen Link zu
 * schreiben.
 *
 * Was hier NICHT hineingehört, obwohl es sich verstellen lässt: Kameraposition,
 * sichtbare Ebenen, Auswahl und Reihenfolge der Diagrammspuren, Aufnahmefenster
 * des Recorders, Farbschema. Das ist Bedienzustand. Er darf gespeichert werden,
 * aber getrennt — er verändert kein Rechenergebnis, und sobald sich beides
 * mischt, ist nicht mehr erkennbar, was eine Zahl beeinflusst.
 */

/**
 * Was die Maschine tut.
 *
 * Die ersten vier sind GÄNGE: sie haben eine Schrittlänge, einen
 * Standphasenanteil und zwei Beine, die eine halbe Periode auseinanderlaufen.
 * Die letzten drei nicht — `stand` ist eine Haltung, `hop` und `pick` sind
 * Skripte mit gleichphasigen Beinen. Der Unterschied steht nicht nur hier: er
 * entscheidet, welchen Weg `referenceAngle` nimmt.
 */
export type GaitKind = 'walk' | 'carry' | 'run' | 'sprint' | 'stand' | 'hop' | 'pick'

/**
 * Regler des Referenzgangs.
 *
 * Faktoren sind dimensionslos und multiplizieren die Amplitude der Normkurve.
 * `cadence` in Schritten je Minute, `lean` in Grad.
 */
export interface GaitTrim {
  cadence: number
  step: number
  knee: number
  push: number
  pelvis: number
  tilt: number
  arm: number
  asym: number
  lean: number
}

/**
 * Die Gangaufgabe des gerechneten Gangs.
 *
 * Im gerechneten Betrieb entsteht die Bewegung aus dieser Aufgabe statt aus
 * Winkeltabellen. Nur diese Zahlen unterscheiden die Gangarten — die Kette
 * dahinter ist jedes Mal dieselbe.
 */
export interface GaitTask {
  /** Schrittlänge in Metern. */
  step: number
  /** Standphasenanteil, 0 … 1. */
  duty: number
  /** Fussfreigang in der Schwungphase, Meter. */
  clear: number
  /** Beinstreckung in der Standmitte, Anteil der gestreckten Beinlänge. */
  flex: number
  /** Absenkung des Beckens beim Aufsetzen, Meter. */
  bob: number
  /** Aufsetzwinkel des Fusses, Zehen hoch, Grad. */
  touch: number
  /** Abstosswinkel, Zehen runter, Grad. */
  push: number
  /** Armschwung als Anteil des Hüftwinkels. */
  arm: number
  /** Seitliche Verlagerung über das Standbein, Meter. */
  sway: number
  /** Halbe Spurbreite am Boden, Meter. */
  track: number
  /** Beckenneigung nach vorn, Grad. */
  lean: number
  /** Beckendrehung um die Hochachse, Amplitude in Grad. */
  yaw: number
}

/** Einzeln zuschaltbare Anteile des Modells. */
export type FeatureKey =
  | 'pelSag'
  | 'pelFro'
  | 'pelTra'
  | 'hipAbd'
  | 'frontal'
  | 'mtp'
  | 'trunk'
  | 'gaze'
  | 'arms'
  | 'zmp'
  | 'toeSpring'
  | 'recSpring'
  | 'recBus'
  | 'hold'
  | 'comply'

/**
 * Der Zustand einer stillgelegten Winkelfreiheit.
 *
 * Sie steht hier bei den übrigen Eingaben und nicht im Modell: „was passiert,
 * wenn das Knie ausfällt" ist eine Frage an dieselbe Konstruktion und nicht
 * eine zweite Konstruktion. Wer eine Achse wirklich weglassen will, nimmt sie
 * aus dem Modell.
 *
 * Was das Ausschalten bewirkt, steht in `off.ts`.
 */
export interface OffState {
  /** Winkel, auf dem die Achse feststeht, Grad — in der Zählweise der Winkelfreiheit. */
  at: number
  /**
   * Bleibt der Antrieb verbaut?
   *
   * `true` — die Achse ist gesperrt, der Antrieb hängt weiter dran und wiegt.
   * Er hält nur noch, statt zu bewegen. Das ist die Frage „was, wenn eine
   * Achse ausfällt".
   *
   * `false` — der Antrieb ist ausgebaut. Masse, Kosten und Leistungsbedarf
   * fallen weg, das Moment trägt die Struktur. Das ist die Frage „was, wenn
   * wir diese Achse weglassen", und das ist die interessantere der beiden.
   */
  drive: boolean
}

/**
 * Welche Winkelfreiheiten stillstehen, nach ihrer Kennung im Modell.
 *
 * Der Schlüssel ist die Kennung der Winkelfreiheit (`knee`, `hipR`, …) und
 * nicht die einer einzelnen Ebene: eine Freiheit ist eine Baugruppe, und
 * ausgeschaltet wird die Baugruppe. Die Hüfte besteht aus drei davon und lässt
 * sich deshalb einzeln in Gieren, Rollen und Nicken stilllegen.
 */
export type OffSet = Record<string, OffState>

export interface SimState {
  /**
   * ABGLEICHSWERT der Eigenmasse, kg — keine Rechengrösse.
   *
   * Die Eigenmasse, mit der gerechnet wird, ist die Summe der Einzelmassen
   * (`ownMass`). Dieser Wert steht daneben: er ist die Zielvorgabe oder der
   * Wägewert, gegen den die Summe geprüft wird. Die Abweichung wird gezeigt,
   * nie verrechnet — eine Bilanz, die sich selbst korrigiert, verbirgt genau
   * den Fehler, den sie finden soll.
   *
   * Die Vorlage macht es andersherum: dort ist die Körpermasse die Eingabe und
   * die Segmentmassen sind Anteile davon. Das ist ein Menschmodell. Bei einer
   * Konstruktion kommen die Kilogramm aus der Stückliste, und wer ein Bauteil
   * schwerer macht, will nicht, dass dabei still alle anderen mitwachsen.
   */
  body: number
  /**
   * Zuladung, kg — eine echte Eingabe. Wirkt nur im Modus Tragen.
   *
   * Sie steht im Zustand und nicht im Modell, weil sie zum Szenario gehört und
   * nicht zur Maschine. Wo sie sitzt, sagt das Modell (`masses.payload`).
   */
  load: number
  gait: GaitKind
  /** Kennung des gewählten Gangbilds innerhalb der Gangart. */
  preset: string
  /**
   * false: vorgeschriebener Gang aus den Normkurven — der Nullzustand.
   * true: gerechneter Gang aus der Aufgabe.
   */
  generated: boolean
  trim: GaitTrim
  task: GaitTask
  features: Record<FeatureKey, boolean>
  /** Stillgelegte Winkelfreiheiten. */
  off: OffSet
  /** Stützstellen je Zyklus. */
  samples: number
}

/** Regler-Vorbelegung. */
export const DEFAULT_TRIM: GaitTrim = {
  cadence: 112,
  step: 1,
  knee: 1,
  push: 1,
  pelvis: 1,
  tilt: 1,
  arm: 1,
  asym: 0,
  lean: 0,
}

/**
 * Grenzen der Regler.
 *
 * Stehen hier und nicht in der Oberfläche: ein Schieberegler soll nicht
 * entscheiden dürfen, was rechnerisch zulässig ist. Die Oberfläche liest diese
 * Tabelle.
 */
export interface TrimSpec {
  key: keyof GaitTrim
  label: string
  min: number
  max: number
  step: number
  /** Einheit für die Anzeige. Leer heisst: dimensionsloser Faktor. */
  unit: string
}

export const TRIM_SPECS: readonly TrimSpec[] = [
  { key: 'cadence', label: 'Kadenz', min: 70, max: 200, step: 1, unit: 'spm' },
  { key: 'step', label: 'Schrittlänge', min: 0.45, max: 1.45, step: 0.01, unit: '' },
  { key: 'knee', label: 'Knieflexion', min: 0.25, max: 1.4, step: 0.01, unit: '' },
  { key: 'push', label: 'Push-off', min: 0.2, max: 1.6, step: 0.01, unit: '' },
  { key: 'pelvis', label: 'Beckenbewegung', min: 0, max: 2.2, step: 0.01, unit: '' },
  { key: 'tilt', label: 'Beckenkippung', min: 0, max: 3.5, step: 0.05, unit: '' },
  { key: 'arm', label: 'Armschwung', min: 0, max: 1.7, step: 0.01, unit: '' },
  { key: 'asym', label: 'Asymmetrie L/R', min: -0.4, max: 0.4, step: 0.01, unit: '' },
  { key: 'lean', label: 'Rumpfneigung', min: -8, max: 14, step: 0.5, unit: '°' },
] as const

/**
 * Aufgabensätze je Gangart.
 *
 * `stand` und `hop` haben keinen eigenen Satz — Stehen kennt keine Schrittlänge,
 * und der Hackensprung ist als Stützstellenbewegung gebaut, nicht als Gangaufgabe.
 * Beide fallen auf `walk` zurück, wenn der gerechnete Betrieb eingeschaltet wird.
 */
/**
 * Trägt der Roboter gerade etwas?
 *
 * Sie stand im Bodenkontakt, weil sie dort zuerst gebraucht wurde. Sie ist aber
 * eine Aussage über den ZUSTAND und nicht über den Boden — und seit die
 * Ganggenerierung dieselbe Frage stellt (die Arme halten dann die Kiste, statt
 * zu schwingen), musste sie hierher: `ground.ts` liegt hinter `gait.ts`, ein
 * Import zurück wäre ein Ring.
 *
 * BEIDE BEDINGUNGEN ZÄHLEN. „Tragen" mit Zuladung null ist Gehen mit einem
 * anderen Namen; wer dann die Kiste einblendet oder die Arme anhebt, zeigt eine
 * Last, die es nicht gibt.
 */
export const carrying = (state: SimState): boolean => state.gait === 'carry' && state.load > 0

export const GAIT_TASKS: Record<'walk' | 'carry' | 'run' | 'sprint', GaitTask> = {
  walk: {
    step: 0.5,
    duty: 0.6,
    clear: 0.075,
    flex: 0.99,
    bob: 0.052,
    touch: 18,
    push: -25,
    arm: 0.55,
    sway: 0.05,
    track: 0.1,
    lean: 0,
    yaw: 4,
  },
  carry: {
    step: 0.43,
    duty: 0.64,
    clear: 0.062,
    flex: 0.978,
    bob: 0.044,
    touch: 14,
    push: -20,
    arm: 0.1,
    sway: 0.048,
    track: 0.115,
    lean: 2,
    yaw: 3,
  },
  run: {
    step: 0.7,
    duty: 0.34,
    clear: 0.13,
    flex: 0.968,
    bob: 0.076,
    touch: 6,
    push: -32,
    arm: 0.85,
    sway: 0.046,
    track: 0.086,
    lean: 4,
    yaw: 5,
  },
  sprint: {
    step: 0.92,
    duty: 0.24,
    clear: 0.17,
    flex: 0.958,
    bob: 0.092,
    touch: -4,
    push: -38,
    arm: 1,
    sway: 0.044,
    track: 0.076,
    lean: 6,
    yaw: 6,
  },
}

/**
 * Reglervorbelegung je Gangart.
 *
 * Der Sprint ist auf 4,0 m/s gesetzt. Die aufgezeichneten Sprintlinien stammen
 * von einem Menschen an der Leistungsgrenze; unverändert übernommen ergaben sie
 * 8,4 m/s und 1,94 m Schrittlänge — bei 884 mm Bein ist das keine Bewegung, die
 * diese Maschine ausführen kann. Der Schrittfaktor ist deshalb auf 0,464
 * gesetzt: die Form der Linien bleibt, die Amplitude passt zur Aufgabe.
 */
export const SPRINT_STEP = 0.464

export function defaultTrim(gait: GaitKind): GaitTrim {
  switch (gait) {
    /* Hackensprung und Aufheben haben keine Kadenz im Sinne von Schritten je
       Minute — die Zahl ist bei ihnen die Zyklusdauer der Bewegung. 60 spm
       heisst hier: zwei Sekunden je Durchgang. */
    case 'hop':
      return { ...DEFAULT_TRIM, cadence: 100, step: 1, arm: 1 }
    case 'pick':
      return { ...DEFAULT_TRIM, cadence: 60, step: 1, arm: 1 }
    case 'stand':
      return { ...DEFAULT_TRIM, cadence: 100, step: 1, arm: 1 }
    case 'sprint':
      return { ...DEFAULT_TRIM, cadence: 240, step: SPRINT_STEP, lean: -4 }
    case 'run':
      return { ...DEFAULT_TRIM, cadence: 170 }
    case 'carry':
      return { ...DEFAULT_TRIM, cadence: 108, step: 0.96 }
    default:
      return { ...DEFAULT_TRIM }
  }
}

/**
 * Das Gangbild, mit dem eine Bewegung beginnt.
 *
 * Als Tabelle und nicht als verschachtelte Bedingung: mit sieben Bewegungen war
 * die Kette aus Fragezeichen nicht mehr zu lesen, und ein fehlender Zweig fällt
 * darin nicht auf.
 */
export const DEFAULT_PRESET: Record<GaitKind, string> = {
  walk: 'normal',
  carry: 'karton',
  run: 'jog',
  sprint: 'vmax',
  stand: 'neutral',
  hop: 'hop',
  pick: 'pick',
}

export function defaultTask(gait: GaitKind): GaitTask {
  if (gait === 'carry' || gait === 'run' || gait === 'sprint') return { ...GAIT_TASKS[gait] }
  return { ...GAIT_TASKS.walk }
}

export const DEFAULT_FEATURES: Record<FeatureKey, boolean> = {
  pelSag: true,
  pelFro: true,
  pelTra: true,
  hipAbd: true,
  frontal: true,
  /* Gen5: der Fuss Foot_V5 ist starr — kein Zehengrundgelenk. Das Merkmal
     bleibt schaltbar, damit man sieht, was ein Vorfussrocker brächte. */
  mtp: false,
  trunk: true,
  gaze: true,
  arms: true,
  zmp: true,
  toeSpring: true,
  recSpring: true,
  recBus: true,
  hold: true,
  comply: true,
}

export function defaultState(gait: GaitKind = 'walk'): SimState {
  return {
    body: 55,
    load: 20,
    gait,
    preset: DEFAULT_PRESET[gait],
    generated: false,
    trim: defaultTrim(gait),
    task: defaultTask(gait),
    features: { ...DEFAULT_FEATURES },
    off: {},
    samples: 120,
  }
}
